import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Routes, Route, useLocation, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import "./App.css";

import Home from "./pages/Home";
import Catalog from "./pages/Catalog";
import Shop from "./pages/Shop";
import ComingSoon from "./components/ComingSoon";
import Subscribe from "./pages/Subscribe";
import Contact from "./pages/Contact";
import Admin from "./pages/Admin";

import Terms from "./pages/Terms";
import Legal from "./pages/Legal";
import Privacy from "./pages/Privacy";
import Refund from "./pages/Refund";

import ClosedPreDrop from "./pages/ClosedPreDrop";
import ClosedPostDrop from "./pages/ClosedPostDrop";

import LegalLinks from "./components/LegalLinks";
import AutoplayVideo from "./components/AutoplayVideo";
import Analytics from "./components/Analytics";
import { useHomeVideo } from "./config/HomeVideos";
import BagButton from "./components/BagButton";
import { useNavDirection } from "./NavDirectionContext";
import { getSiteState } from "./config/Drop";

const DIST = "100%";

function PageWrap({ dir, variant = "slide", children }) {
  const slideVariants = {
    initial: (d) => ({ y: d === "up" ? DIST : `-${DIST}` }),
    animate: { y: 0 },
    exit: (d) => ({ y: d === "up" ? `-${DIST}` : DIST }),
  };

  const fadeVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  };

  const isFade = variant === "fade";
  const variants = isFade ? fadeVariants : slideVariants;

  const transition = isFade
    ? { duration: 0.35, ease: "easeInOut" }
    : { type: "tween", duration: 0.42, ease: [0.76, 0, 0.24, 1] };

  return (
    <motion.div
      custom={dir}
      variants={variants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={transition}
      style={{
        position: "absolute",
        inset: 0,
        willChange: isFade ? "opacity" : "transform",
      }}
    >
      {children}
    </motion.div>
  );
}

export default function App() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { dir, setDir } = useNavDirection();

  const [siteState, setSiteState] = useState(() => getSiteState(new Date()));

  useEffect(() => {
    const t = setInterval(() => {
      setSiteState(getSiteState(new Date()));
    }, 500);
    return () => clearInterval(t);
  }, []);

  // ✅ Preview bypass sécurisé : ?preview=<SECRET> (Vercel env REACT_APP_PREVIEW_KEY)
  // ?preview=0 désactive. Persiste en localStorage (valeur = le secret).
  const PREVIEW_KEY = "GS_PREVIEW";
  const PREVIEW_SECRET = process.env.REACT_APP_PREVIEW_KEY;
  useEffect(() => {
    const p = searchParams.get("preview");
    if (p === "0") {
      localStorage.removeItem(PREVIEW_KEY);
    } else if (p && PREVIEW_SECRET && p === PREVIEW_SECRET) {
      localStorage.setItem(PREVIEW_KEY, PREVIEW_SECRET);
    }
  }, [searchParams, PREVIEW_SECRET]);
  const preview =
    Boolean(PREVIEW_SECRET) &&
    localStorage.getItem(PREVIEW_KEY) === PREVIEW_SECRET;

  // ✅ Admin gate (ENV + query param)
  // Vercel env var: REACT_APP_ADMIN_KEY
  const ADMIN_KEY = process.env.REACT_APP_ADMIN_KEY;
  const okAdmin =
    Boolean(searchParams.get("key")) &&
    Boolean(ADMIN_KEY) &&
    searchParams.get("key") === ADMIN_KEY;

  // ✅ Override via URL: ?state=open | closed_pre | closed_post
  //
  // ⚠️ Réservé au mode preview. Avant le 17/09/2026 ce paramètre marchait sans
  // aucune clé : n'importe qui pouvait poser ?state=open et ouvrir la boutique
  // avant l'heure du drop. Il est maintenant conditionné à `preview`, donc au
  // secret REACT_APP_PREVIEW_KEY, exactement comme le bypass du gate.
  //
  // ⚠️ Et ça ne vaut pas verrou. REACT_APP_PREVIEW_KEY est inlinée en clair
  // dans le bundle public au build : qui lit le JS trouve la valeur. Aucun gate
  // écrit côté navigateur ne peut protéger un drop. La seule barrière réelle
  // est côté Shopify — produit non publié, ou stock à zéro, jusqu'à l'heure.
  const forced = preview ? searchParams.get("state") : null;
  const effectiveState =
    forced === "open" || forced === "closed_pre" || forced === "closed_post"
      ? forced
      : siteState;

  const isClosedPre = effectiveState === "closed_pre";
  const isClosedPost = effectiveState === "closed_post";

  const Guarded = ({ children }) => {
    // Le preview ouvre tout... sauf si un ?state= explicite dit le contraire :
    // sans cette exception, `preview` court-circuiterait le gate et on ne
    // pourrait plus jamais prévisualiser les pages fermées.
    if (preview && !forced) return children;
    if (isClosedPre) return <ClosedPreDrop />;
    if (isClosedPost) return <ClosedPostDrop />;
    return children;
  };

  // ✅ Auto-direction : Home -> pages = up, pages -> Home = down
  const prevPathRef = useRef(location.pathname);

  useLayoutEffect(() => {
    const prev = prevPathRef.current;
    const next = location.pathname;

    if (prev === "/" && next !== "/") setDir("up");
    else if (prev !== "/" && next === "/") setDir("down");

    prevPathRef.current = next;
  }, [location.pathname, setDir]);

  // ✅ panier partout sauf Closed pre/post (visible aussi en preview)
  // masqué sur les pages légales pour ne pas chevaucher le sélecteur EN/FR
  const LEGAL_PATHS = ["/terms", "/legal", "/privacy", "/refund"];
  const isLegalPage = LEGAL_PATHS.includes(location.pathname);
  const showCart =
    !isLegalPage && (preview || (!isClosedPre && !isClosedPost));

  // ✅ La barre légale perd son bandeau blanc sur les pages à fond vidéo, pour
  // laisser voir l'image. Les trois pages possibles sur "/" en ont une — Home,
  // ClosedPreDrop et ClosedPostDrop — et depuis le 20/09/2026 Subscribe et
  // Contact aussi. Un bandeau blanc y trancherait sur la vidéo.
  // Les pages légales et le catalogue gardent le bandeau : fond blanc, pas de
  // vidéo derrière.
  // "/admin" y figure aussi : sans clé valide il rend ClosedPreDrop, donc la
  // même page qu'à la racine.
  const VIDEO_PATHS = ["/", "/subscribe", "/contact", "/admin"];
  const legalOverVideo = VIDEO_PATHS.includes(location.pathname);

  // Couleur du texte de la barre, accordée à la vidéo qui est derrière.
  // Depuis le 20/09/2026 les trois pages possibles sur "/" partagent la même
  // vidéo que la home : ClosedPostDrop ne fait plus exception, l'ancienne
  // vidéo claire qui lui imposait un texte sombre n'est plus utilisée.
  const { url: bgVideo, darkText: legalDarkText } = useHomeVideo();

  // ⚠️ On garde la vidéo montée le temps que la transition se termine.
  // location.pathname bascule dès le clic, alors que la page qu'on quitte
  // glisse encore pendant 0,42 s. Démonter la vidéo tout de suite ferait
  // blanchir l'accueil sous nos yeux pendant qu'il sort de l'écran.
  // Au-delà, on la démonte vraiment : la laisser tourner derrière le catalogue
  // ou une page légale, qui ont un fond blanc opaque, ne ferait que décoder une
  // vidéo invisible et vider la batterie.
  const [keepVideo, setKeepVideo] = useState(legalOverVideo);
  useEffect(() => {
    if (legalOverVideo) {
      setKeepVideo(true);
      return;
    }
    const t = setTimeout(() => setKeepVideo(false), 460);
    return () => clearTimeout(t);
  }, [legalOverVideo]);

  return (
    <>
      <Analytics />
      <LegalLinks overVideo={legalOverVideo} darkText={legalDarkText} />
      <BagButton visible={showCart} position="right" />

      <div
        style={{
          position: "relative",
          height: "100vh",
          overflow: "hidden",
          background: "#fff",
        }}
      >
        {/* ⚠️ La vidéo de fond est rendue ICI, une seule fois, et surtout HORS
            de AnimatePresence. Tant qu'elle vivait dans chaque page, naviguer
            la démontait et en remontait une autre : pendant les 0,42 s du
            glissement, deux <video> du MÊME plan jouaient côte à côte, la
            nouvelle repartant de 0. Mesuré le 20/09/2026 — le fond passait de
            6,99 s à 0,47 s à chaque changement de page. L'image rembobinait
            visiblement, et ça se lisait comme une transition cassée.
            Posée à ce niveau, la balise n'est jamais démontée : le fond reste
            continu et ce sont les pages qui glissent par-dessus.
            Elle n'est pas non plus affectée par le transform des pages, qui
            ferait dériver un position:fixed placé à l'intérieur. */}
        {keepVideo && bgVideo && (
          <AutoplayVideo key={bgVideo} src={bgVideo} className="appBgVideo" />
        )}

        <AnimatePresence mode="sync" initial={false}>
          <Routes location={location} key={location.pathname}>
            {/* ✅ ADMIN protégé : /admin?key=... */}
            <Route
              path="/admin"
              element={
                okAdmin ? (
                  <PageWrap dir={dir}>
                    <Admin />
                  </PageWrap>
                ) : (
                  <PageWrap dir={dir}>
                    <ClosedPreDrop />
                  </PageWrap>
                )
              }
            />

            {/* ✅ Legal pages: accessibles même quand le site est closed */}
            <Route
              path="/terms"
              element={
                <PageWrap dir={dir} variant="fade">
                  <Terms />
                </PageWrap>
              }
            />
            <Route
              path="/legal"
              element={
                <PageWrap dir={dir} variant="fade">
                  <Legal />
                </PageWrap>
              }
            />
            <Route
              path="/privacy"
              element={
                <PageWrap dir={dir} variant="fade">
                  <Privacy />
                </PageWrap>
              }
            />
            <Route
              path="/refund"
              element={
                <PageWrap dir={dir} variant="fade">
                  <Refund />
                </PageWrap>
              }
            />

            <Route
              path="/"
              element={
                <PageWrap dir={dir}>
                  <Guarded>
                    <Home />
                  </Guarded>
                </PageWrap>
              }
            />

            <Route
              path="/shop"
              element={
                <PageWrap dir={dir}>
                  <Guarded>
                    <Catalog />
                  </Guarded>
                </PageWrap>
              }
            />

            <Route
              path="/shop/bonnet"
              element={
                <PageWrap dir={dir}>
                  <Guarded>
                    <Shop />
                  </Guarded>
                </PageWrap>
              }
            />

            <Route
              path="/shop/polo"
              element={
                <PageWrap dir={dir}>
                  <Guarded>
                    <ComingSoon name="polo" price="TBA" />
                  </Guarded>
                </PageWrap>
              }
            />

            <Route
              path="/shop/tee"
              element={
                <PageWrap dir={dir}>
                  <Guarded>
                    <ComingSoon name="tee-shirt" price="TBA" />
                  </Guarded>
                </PageWrap>
              }
            />

            <Route
              path="/subscribe"
              element={
                <PageWrap dir={dir}>
                  <Guarded>
                    <Subscribe />
                  </Guarded>
                </PageWrap>
              }
            />

            <Route
              path="/contact"
              element={
                <PageWrap dir={dir}>
                  <Guarded>
                    <Contact />
                  </Guarded>
                </PageWrap>
              }
            />
          </Routes>
        </AnimatePresence>
      </div>
    </>
  );
}